import cx from 'classnames'
import { FromURL } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromURL'
import { ContentSourcePickerVariant } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/Types'
import { Text } from 'almanac-ui/icons'
import { FromText } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromText'
import { FromDoc } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromDoc'
import { FromFile } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromFile'
import { Item } from '@react-stately/collections'
import { Tabs } from 'components/BlazeTabs/Tabs'
import { Importer } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/ContentSourcePicker'
import Styles from './CampaignWizard.module.scss'
import { Document, Import, Link } from 'almanac-ui/icons'
import { ReactText, useState } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BareArticle } from 'components/CampaignCreationModal/CampaignCreationModal'
import { SourceCard } from 'components/SourceCard/SourceCard'
import { useObjectRef } from '@react-aria/utils'

type Value = {
  importer: Importer
  value: string
  doc?: BareArticle | undefined
  filename?: string
}

type Props = {
  value: Value
  onChange: (value: Value) => void
}

export const SourcePicker = ({ value, onChange }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const [urlExamplesVisible, setUrlExamplesVisible] = useState(true)
  const [textExamplesVisible, setTextExamplesVisible] = useState(true)
  const fromDocRef = useObjectRef<HTMLInputElement>(null)

  const changeImporter = (key: ReactText) => {
    onChange({ importer: key as Importer, value: '' })
  }

  if (value.importer === Importer.SelectDoc && value.doc) {
    return (
      <SourceCard
        icon={value.doc.emoji ?? <Document />}
        title={value.doc.name}
        onDismiss={() => {
          onChange({ importer: Importer.SelectDoc, value: '' })
          fromDocRef.current?.focus()
        }}
      />
    )
  }

  if (value.importer === Importer.UploadFiles && value.filename) {
    return (
      <SourceCard
        icon={<Document />}
        title={value.filename}
        onDismiss={() => onChange({ importer: Importer.UploadFiles, value: '' })}
      />
    )
  }

  return (
    <Tabs
      selectedKey={value.importer}
      onSelectionChange={changeImporter}
      classNames={{ tabs: Styles.tabs, tab: Styles.tab }}
    >
      <Item
        key={Importer.ScanWebsite}
        title={
          <div className={Styles.tabContents}>
            <Link />
            <span className={cx({ [Styles.hidden]: isMobile && value.importer !== Importer.ScanWebsite })}>
              Link Website
            </span>
          </div>
        }
      >
        <FromURL
          type="campaign"
          showExamples
          sampleText={value.value}
          onChange={(text) => onChange({ importer: Importer.ScanWebsite, value: text })}
          setExamplesVisible={setUrlExamplesVisible}
          examplesVisible={urlExamplesVisible}
        />
      </Item>
      <Item
        key={Importer.AddText}
        title={
          <div className={Styles.tabContents}>
            <Text />
            <span className={cx({ [Styles.hidden]: isMobile && value.importer !== Importer.AddText })}>Paste Text</span>
          </div>
        }
      >
        <FromText
          sampleText={value.value}
          setExamplesVisible={setTextExamplesVisible}
          examplesVisible={textExamplesVisible}
          onChange={(text) => onChange({ importer: Importer.AddText, value: text })}
          variant={ContentSourcePickerVariant.CampaignImport}
        />
      </Item>
      <Item
        key={Importer.SelectDoc}
        title={
          <div className={Styles.tabContents}>
            <Document />
            <span className={cx({ [Styles.hidden]: isMobile && value.importer !== Importer.SelectDoc })}>
              Select Doc
            </span>
          </div>
        }
      >
        <FromDoc
          selectedDoc={value.doc}
          onChange={(text, doc) => onChange({ importer: Importer.SelectDoc, value: text, doc })}
          fullWidth
          ref={fromDocRef}
        />
      </Item>
      <Item
        key={Importer.UploadFiles}
        title={
          <div className={Styles.tabContents}>
            <Import />
            <span className={cx({ [Styles.hidden]: isMobile && value.importer !== Importer.UploadFiles })}>
              Upload Files
            </span>
          </div>
        }
      >
        <FromFile onChange={(text, filename) => onChange({ importer: Importer.UploadFiles, value: text, filename })} />
      </Item>
    </Tabs>
  )
}
